/**
 * @fileoverview Turning a written phrase into something shouted.
 * @module lib/domain/shout
 *
 * @description
 * The app does not *say* the phrase, it hurls it. Two things make that
 * happen: the text is rewritten so the synthesiser attacks it, and each mode
 * gets its own voice — Peak climbs, Testo shoves.
 */

import { AppMode, type VoiceSettings } from "@/types/types"

/** Voice used for each mode. */
export const VOICE_BY_MODE: Readonly<Record<AppMode, VoiceSettings>> = {
	// Lower and slower: a line delivered from the top of something.
	[AppMode.PEAK]: { lang: "fr-FR", rate: 0.95, pitch: 0.75, volume: 1 },
	// Faster and heavier: a line delivered two centimetres from your face.
	[AppMode.TESTO]: { lang: "fr-FR", rate: 1.2, pitch: 0.45, volume: 1 },
}

/**
 * Rewrites a phrase the way it should be read on screen when shouted.
 *
 * @param text - The phrase as written in the catalogue.
 * @returns The same phrase, upper-cased and with its spacing collapsed.
 *
 * @example
 * toShout("Encore une.") // => "ENCORE UNE."
 */
export function toShout(text: string): string {
	return text.trim().replace(/\s+/g, " ").toLocaleUpperCase("fr-FR")
}

/**
 * Rewrites a phrase the way the synthesiser should attack it.
 *
 * @description
 * Speech engines read `!` as an attack and a full stop as a fall. Replacing
 * the final punctuation with `!` is what turns a sentence into a shout, and
 * doubling it does nothing — engines cap the effect, so one is enough.
 *
 * @param text - The phrase as written in the catalogue.
 * @returns The utterance to hand to the synthesiser.
 *
 * @example
 * toUtterance("Encore une.") // => "ENCORE UNE !"
 */
export function toUtterance(text: string): string {
	const shouted = toShout(text)
	if (shouted === "") return ""

	// French sets a space before `?` and `!`, so the trailing whitespace has
	// to go with the punctuation or the shout ends up double-spaced.
	return `${shouted.replace(/[\s.!?…]+$/u, "")} !`
}

/**
 * The voice a mode is shouted with.
 *
 * @param mode - The active mode.
 * @returns Its voice settings.
 */
export function voiceFor(mode: AppMode): VoiceSettings {
	return VOICE_BY_MODE[mode]
}
